import { useTheme } from "@emotion/react";
import { Box, Stack, Typography } from "@mui/material";
import { BarItem, BarItemProps, ResponsiveBar } from "@nivo/bar";
import { createContext, useContext, useEffect, useState } from "react";
import { defaultNumberFormatter } from "../utils/numberDisplay";
import { getForegroundColor } from "./colors";
import { barChartBoxStyling } from "./styling";

type Props = {
    data: Array<any>;
    keys: Array<string>;
    indexBy: string;
    colors: Array<string>;
    title: string;
    boldSpan: string;
    isSystemCleared: boolean;
    animate?: boolean;
    renderTitle?: boolean;
    numberFormatter?: (value: number | null | undefined) => string;
    legendTotalInOneColumn: number;
};

const KeysContext = createContext<Array<any>>([]);
const NumberFormatterContext = createContext(defaultNumberFormatter);

const BarWithTotals = (props: BarItemProps<{ [key: string]: number }>) => {
    const theme = useTheme();
    const keys = useContext(KeysContext);
    const numberFormatter = useContext(NumberFormatterContext);

    const dataId = props.bar.data.id;

    const actualKeys = Object.keys(props.bar.data.data).filter((e) => e !== "total" && e !== "type");
    const actualKeysOrdered = keys.filter((k) => actualKeys.indexOf(k) !== -1);
    const lastDataId = actualKeysOrdered[actualKeysOrdered.length - 1];

    if (dataId === lastDataId) {
        // Last key
        const bar = props.bar;

        // Calculate the x-position for the text based on the bar width
        const textX = bar.x + bar.width / 2;
        const textY = bar.y - 10;

        const total = bar.data.data.total;

        return (
            <>
                <BarItem {...props} />
                <g transform={`translate(${textX}, ${textY})`}>
                    <text
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                            ...theme,
                            pointerEvents: "none",
                        }}
                    >
                        {numberFormatter(total)}
                    </text>
                </g>
            </>
        );
    } else {
        return <BarItem {...props} />;
    }
};

export const BarChart = ({
    legendTotalInOneColumn,
    data,
    keys,
    indexBy,
    colors,
    title,
    isSystemCleared,
    boldSpan,
    animate,
    renderTitle,
    numberFormatter,
}: Props) => {
    const [tickRotation, setTickRotation] = useState(0);
    const marginStyling = { top: 80, right: 50, bottom: 35, left: 50 };
    const numberFormatterInternal = numberFormatter ? numberFormatter : defaultNumberFormatter;

    const chartTheme = {
        fontFamily: "Inter, Arial, sans-serif",
        labels: {
            text: {
                fontSize: 14,
                fontWeight: 500,
            },
        },
    };

    useEffect(() => {
        const handleResize = () => {
            // Adjust tick rotation based on breakpoint
            const breakpoint = 875; // Change this value to your desired breakpoint
            const newTickRotation = window.innerWidth < breakpoint ? 10 : 0;
            setTickRotation(newTickRotation);
        };

        // Initial setup
        handleResize();

        // Add event listener for window resize
        window.addEventListener("resize", handleResize);

        // Clean up the event listener on unmount
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return (
        <KeysContext.Provider value={keys}>
            <NumberFormatterContext.Provider value={numberFormatterInternal}>
                <Box sx={barChartBoxStyling}>
                    <ResponsiveBar
                        animate={animate}
                        data={data}
                        keys={keys}
                        indexBy={indexBy}
                        margin={marginStyling}
                        padding={0.3}
                        layout="vertical"
                        innerPadding={1}
                        enableGridY={false}
                        enableGridX={false}
                        valueScale={{ type: "linear" }}
                        indexScale={{ type: "band", round: true }}
                        theme={chartTheme}
                        colors={colors}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: tickRotation,
                            legendPosition: "middle",
                            legendOffset: 32,
                        }}
                        axisLeft={null}
                        labelSkipWidth={12}
                        labelSkipHeight={20}
                        labelTextColor={getForegroundColor}
                        label={(d) => `${isSystemCleared && d.id === "The Coca-Cola System" ? "" : numberFormatterInternal(d.value)}`}
                        valueFormat={(d) => numberFormatterInternal(d)}
                        barComponent={BarWithTotals}
                    />
                    <Stack direction="row" justifyContent="space-between" width="70%">
                        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" flexWrap="wrap" mt={1}>
                            {keys.map(
                                (k, i) =>
                                    i <= legendTotalInOneColumn && (
                                        <Stack key={i} direction="row" alignItems="center" justifyContent="center" mt={1.5}>
                                            <Box
                                                width="12px"
                                                height="12px"
                                                sx={{
                                                    backgroundColor: colors[i],
                                                    mr: "0.3rem",
                                                }}
                                            />
                                            <Typography mr={2} variant="caption">
                                                {k}
                                            </Typography>
                                        </Stack>
                                    )
                            )}
                        </Stack>
                        <Stack direction="column" alignItems="flex-start" justifyContent="flex-start" flexWrap="wrap" mt={1}>
                            {keys.map(
                                (k, i) =>
                                    i > legendTotalInOneColumn && (
                                        <Stack key={i} direction="row" alignItems="center" justifyContent="center" mt={1.5}>
                                            <Box
                                                width="12px"
                                                height="12px"
                                                sx={{
                                                    backgroundColor: colors[i],
                                                    mr: "0.3rem",
                                                }}
                                            />
                                            <Typography mr={2} variant="caption">
                                                {k}
                                            </Typography>
                                        </Stack>
                                    )
                            )}
                        </Stack>
                    </Stack>
                </Box>
            </NumberFormatterContext.Provider>
        </KeysContext.Provider>
    );
};
